import React, { useContext } from "react";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/cartpage.scss";
import { Link, useNavigate } from "react-router-dom";
import Footer from "./Footer";
import Header from "./Header";
import {
  FaChevronRight,
  FaTrash,
  FaMinus,
  FaPlus,
  FaLock,
  FaShieldAlt,
  FaCheck,
} from "react-icons/fa";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);

  const getProductById = (id) => {
    return productData.find((product) => product.id === id);
  };

  const calculateSubtotal = () => {
    let total = 0;
    cartItems.forEach((item) => {
      const product = getProductById(item.id);
      if (product) {
        const price = parseFloat(product.price);
        total += price * (item.quantity || 1);
      }
    });
    return total;
  };

  const subtotal = calculateSubtotal();
  const shipping = subtotal >= 59 ? 0 : 9.99;
  const total = subtotal + shipping;

  const handleUpdateQuantity = (id, size, color, newQuantity) => {
    if (newQuantity > 0) {
      updateQuantity(id, size, color, newQuantity);
    }
  };

  const handleRemoveItem = (id, size, color) => {
    removeFromCart(id, size, color);
  };

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      navigate("/checkout");
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      alert("Your cart is empty");
    }
  };

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="cart-page">
      <Header />

      <div className="cart-content">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/">Home</Link>
          <FaChevronRight className="breadcrumb-separator" />
          <Link to="/shop">Dog</Link>
          <FaChevronRight className="breadcrumb-separator" />
          <Link to="/shop">Treats & Chews</Link>
          <FaChevronRight className="breadcrumb-separator" />
          <Link to="/shop">Natural Chews</Link>
          <FaChevronRight className="breadcrumb-separator" />
          <span className="current">Beef Strips</span>
        </div>

        {/* Page Title */}
        <h1 className="page-title">Your Shopping Cart</h1>

        {cartItems.length === 0 ? (
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added anything to your cart yet.</p>
            <Link to="/shop" className="continue-shopping-btn" onClick={toTop}>
              Continue Shopping
            </Link>
          </div>
        ) : (
          <div className="cart-container">
            {/* Cart Items Table */}
            <div className="cart-items-section">
              <div className="cart-table-header">
                <span className="col-product">Item</span>
                <span className="col-price">Price</span>
                <span className="col-qty">Qty</span>
                <span className="col-total">Total</span>
              </div>

              {cartItems.map((item, index) => {
                const product = getProductById(item.id);
                if (!product) return null;

                const itemTotal =
                  parseFloat(product.price) * (item.quantity || 1);

                return (
                  <div key={`${item.id}-${index}`} className="cart-item">
                    <div className="col-product">
                      <Link
                        to={`/shopDetails/${item.id}`}
                        className="product-image-link"
                        onClick={toTop}
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/100x100?text=No+Image";
                          }}
                        />
                      </Link>
                      <div className="product-details">
                        <Link
                          to={`/shopDetails/${item.id}`}
                          className="product-name"
                          onClick={toTop}
                        >
                          {product.name}
                        </Link>
                        <button
                          className="remove-btn"
                          onClick={() =>
                            handleRemoveItem(
                              item.id,
                              item.selectedSize,
                              item.selectedColor
                            )
                          }
                        >
                          <FaTrash /> Remove
                        </button>
                      </div>
                    </div>
                    <div className="col-price">
                      <span className="price-label">Price</span>
                      <span className="price-value">${product.price}</span>
                    </div>
                    <div className="col-qty">
                      <span className="qty-label">Qty</span>
                      <div className="quantity-controls">
                        <button
                          className="qty-btn"
                          onClick={() =>
                            handleUpdateQuantity(
                              item.id,
                              item.selectedSize,
                              item.selectedColor,
                              (item.quantity || 1) - 1
                            )
                          }
                        >
                          <FaMinus />
                        </button>
                        <input
                          type="number"
                          value={item.quantity || 1}
                          onChange={(e) =>
                            handleUpdateQuantity(
                              item.id,
                              item.selectedSize,
                              item.selectedColor,
                              parseInt(e.target.value) || 1
                            )
                          }
                          min="1"
                        />
                        <button
                          className="qty-btn"
                          onClick={() =>
                            handleUpdateQuantity(
                              item.id,
                              item.selectedSize,
                              item.selectedColor,
                              (item.quantity || 1) + 1
                            )
                          }
                        >
                          <FaPlus />
                        </button>
                      </div>
                    </div>
                    <div className="col-total">
                      <span className="total-label">Total</span>
                      <span className="total-value">
                        ${itemTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}

              <div className="cart-table-footer">
                <Link
                  to="/shop"
                  className="continue-shopping-link"
                  onClick={toTop}
                >
                  Continue Shopping
                </Link>
              </div>
            </div>

            {/* Order Summary */}
            <div className="order-summary">
              <h2 className="summary-title">Order Summary</h2>

              <div className="summary-rows">
                <div className="summary-row">
                  <span>
                    Subtotal ({cartItems.length}{" "}
                    {cartItems.length === 1 ? "item" : "items"})
                  </span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping</span>
                  <span className="shipping-value">
                    {shipping === 0 ? (
                      <span className="free-shipping">FREE</span>
                    ) : (
                      `$${shipping.toFixed(2)}`
                    )}
                  </span>
                </div>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-total">
                <span>Estimated Total</span>
                <span className="total-amount">${total.toFixed(2)}</span>
              </div>

              <button className="checkout-btn" onClick={handleCheckout}>
                <FaLock className="btn-icon" />
                Secure Checkout
              </button>

              {/* Trust Badges */}
              <div className="trust-badges">
                <div className="trust-badge">
                  <FaShieldAlt className="badge-icon" />
                  <span>Secure Checkout</span>
                </div>
                <div className="trust-badge">
                  <FaCheck className="badge-icon" />
                  <span>Quality Guaranteed</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* We Price Match Section */}
        <div className="price-match-section">
          <div className="price-match-content">
            <div className="price-match-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="price-match-text">
              <h3>We Price Match!</h3>
              <p>
                Found a lower price elsewhere? We'll match it!{" "}
                <Link to="/contact">Contact us</Link> for details.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CartPage;
